import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { apiConfigs, BibleRangePayload } from '../../../../api/APIConfigs';
import { BibleScripture } from '../../../../api/models/BibleVerse';
import { generateBibleRangePayloadFromScriptureIDs } from '../../../../util/bible-study-module';
import { useRequest } from '../../../../util/use-request';
import { SizedBox } from '../../../core/SizedBox';
import { colors, Description, H2 } from '../../../core/Styles';

export interface ScriptureTextProps {
  /** A.K.A. `translationID` in the API */
  version: string;
  /** Example: ['Genesis', '1', '1', '-', 'Genesis', '1', '5'] */
  scriptureIds: string[];
}

export const ScriptureText = (props: ScriptureTextProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [scriptures, setScriptures] = useState<BibleScripture[]>([]);
  const [range, setRange] = useState<BibleRangePayload | null>(null);

  const request = useRequest();

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const range = generateBibleRangePayloadFromScriptureIDs(props.scriptureIds);
        const scripts = await request<BibleScripture[]>(apiConfigs.getBibleRange({ ...range, version: props.version }));
        setRange(range);
        setScriptures(scripts);
      } catch (e: any) {
        toast.error(e.message || 'Failed to load scripture!');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [props.scriptureIds, props.version, request]);

  return (
    <Text>
      {isLoading ? (
        <Description>Loading scriptures...</Description>
      ) : (
        <>
          <H2>
            {range?.book} {range?.fromChapter}
            {`:${range?.fromVerse}`}
            {`-${range?.toChapter}`}
            {`:${range?.toVerse}`}
          </H2>
          <SizedBox height={16} />
          {!!scriptures.length ? (
            <List>
              {scriptures.map(scripture => (
                <Description key={scripture.id}>
                  <sup style={{ fontSize: 10 }}>{scripture.verse}</sup> {scripture.text}
                </Description>
              ))}
            </List>
          ) : (
            <Description color={colors.grey}>Scripture</Description>
          )}
        </>
      )}
    </Text>
  );
};

const Text = styled.div`
  border-radius: 10px;
  border: 1px solid #e2e7ea;
  outline: none;
  padding: 20px;
  max-height: 40vh;
  overflow-y: scroll;
`;

const List = styled.div`
  & > :not(:last-child) {
    margin-bottom: 16px;
  }
`;
